import Box from '@mui/material/Box';
import { alpha, keyframes, styled, type CSSObject, type Theme } from '@mui/material/styles';

import { bgBlur, bgGradient, toneText } from 'src/theme/css';

// ----------------------------------------------------------------------
// The assistant's visual language in one place: a translucent "glass" system
// ----------------------------------------------------------------------

/** Palette keys the glass surfaces can be tinted with. */
export type Tone = 'primary' | 'info' | 'secondary' | 'success' | 'warning' | 'error';

/** Icon sizes, so glyphs stay in step across the panel. */
export const ICON = { xs: 14, sm: 16, md: 18, lg: 22, xl: 26 } as const;

/** Shared scale for round marks and tinted badges. */
export const MARK = { sm: 28, md: 32, lg: 40, xl: 56 } as const;

export type MarkSize = keyof typeof MARK;

export const DRAWER_WIDTH = { default: 440, min: 360, max: 900 } as const;

// -- keyframes ---------------------------------------------------------
// Ring animations read their color from the `--ring-*` custom properties, so
// one keyframe set serves every tint.

/** Slow, continuous halo — the assistant is alive and waiting. */
const breathe = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 var(--ring-strong); }
  50% { box-shadow: 0 0 0 9px var(--ring-fade); }
`;

/** One-shot halo — fired the moment a confirmed action lands. */
const pingOnce = keyframes`
  0% { box-shadow: 0 0 0 0 var(--ring-strong); opacity: 1; }
  100% { box-shadow: 0 0 0 18px var(--ring-fade); opacity: 0; }
`;

/** Overshooting scale-in for a state that just became true. */
const popIn = keyframes`
  0% { transform: scale(0.4); opacity: 0; }
  55% { transform: scale(1.18); opacity: 1; }
  100% { transform: scale(1); }
`;

/** Entrance for cards that interrupt the flow (confirmations, notices). */
export const cardIn = keyframes`
  0% { transform: scale(0.96) translateY(4px); opacity: 0; }
  100% { transform: scale(1) translateY(0); opacity: 1; }
`;

/** Entrance for list items, staggered by index. */
export const tileIn = keyframes`
  0% { transform: translateY(6px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
`;

/** Very subtle breathing scale — reserved for the empty-state hero mark. */
const glowPulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.035); }
`;

/** Entrance for the launcher's first-visit hint chip. */
export const hintIn = keyframes`
  0% { opacity: 0; transform: translateX(8px); }
  100% { opacity: 1; transform: translateX(0); }
`;

// -- style fragments ---------------------------------------------------

const ring = (theme: Theme, tone: Tone, strength: number): CSSObject => ({
  '--ring-strong': alpha(theme.palette[tone].main, strength),
  '--ring-fade': alpha(theme.palette[tone].main, 0),
});

/** Glazed highlight across the top-left of a tinted surface. */
const gloss = (theme: Theme): CSSObject => ({
  content: '""',
  position: 'absolute',
  inset: 0,
  borderRadius: 'inherit',
  ...bgGradient({
    direction: '165deg',
    startColor: `${alpha(theme.palette.common.white, theme.palette.mode === 'light' ? 0.5 : 0.12)} 0%`,
    endColor: 'transparent 60%',
  }),
});

const centered: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

/** The assistant's own gradient — its identity mark, used sparingly. */
export const brandGradient = (theme: Theme, shade: 'main' | 'dark' = 'main') =>
  bgGradient({
    direction: '135deg',
    startColor: theme.palette.primary[shade],
    endColor: theme.palette.info[shade],
  });

/** A tinted, glossy gradient — for the accent-filled hero and send button. */
export const accentGradient = (theme: Theme, tone: Tone = 'primary', deepen = false) =>
  bgGradient({
    direction: '160deg',
    startColor: deepen ? theme.palette[tone].main : theme.palette[tone].light,
    endColor: deepen ? theme.palette[tone].dark : theme.palette[tone].main,
  });

/** Frosted panel: translucent paper over the wash. */
export const glassSurface = (theme: Theme): CSSObject => ({
  ...bgBlur({ color: theme.palette.background.paper, opacity: 0.65, blur: 12 }),
  border: `1px solid ${theme.palette.mode === 'light' ? alpha(theme.palette.common.black, 0.07) : theme.palette.divider}`,
  boxShadow: theme.customShadows.z1,
});

/** Frosted input: the pill shared by the composer and the history search. */
export const glassField = (theme: Theme): CSSObject =>
  theme.palette.mode === 'light'
    ? {
        ...bgBlur({ color: theme.palette.common.white, opacity: 0.55, blur: 16 }),
        border: `1px solid ${alpha(theme.palette.common.white, 0.7)}`,
      }
    : {
        ...bgBlur({ color: theme.palette.background.paper, opacity: 0.8, blur: 16 }),
        border: `1px solid ${theme.palette.divider}`,
      };

// -- surfaces ----------------------------------------------------------

/**
 * The colored ground the whole conversation sits on. Applied once, around both
 * the message list and the composer, so the translucent cards above it have
 * real color to refract instead of floating over plain white.
 */
export const ChatSurface = styled(Box)(({ theme }) => {
  const wash = (tone: Tone, opacity: number, shape: string) =>
    `radial-gradient(ellipse ${shape}, ${alpha(theme.palette[tone].main, opacity)}, transparent 55%)`;

  return {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    background: [
      wash('primary', 0.1, '100% 60% at 15% -10%'),
      wash('info', 0.08, '90% 55% at 100% 10%'),
      wash('secondary', 0.06, '80% 50% at 50% 115%'),
    ].join(','),
  };
});

type MarkProps = {
  size?: MarkSize;
  /** Slow halo, for the idle assistant. */
  pulse?: boolean;
};

/**
 * The gradient circle that stands in for the assistant itself. Exposed as a
 * fragment as well as a component, so it can be layered onto an IconButton
 * without the two fighting over the `size` prop.
 */
export const assistantMark = (theme: Theme, size: MarkSize = 'md', pulse = false): CSSObject => ({
  ...centered,
  ...brandGradient(theme),
  position: 'relative',
  width: MARK[size],
  height: MARK[size],
  borderRadius: '50%',
  color: theme.palette.common.white,
  // The halo lives on a pseudo-element so it can animate box-shadow without
  // fighting whatever elevation the mark itself carries.
  ...(pulse && {
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      ...ring(theme, 'primary', 0.3),
      animation: `${breathe} 2.4s ease-in-out infinite`,
    },
  }),
});

export const AssistantMark = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'size' && prop !== 'pulse',
})<MarkProps>(({ theme, size = 'md', pulse }) => assistantMark(theme, size, pulse));

type BadgeProps = {
  tone?: Tone;
  size?: MarkSize;
  /** Squircle instead of a circle. */
  rounded?: boolean;
  /** Faster halo, for work in progress. */
  pulse?: boolean;
  /** Scale-in, for a state that just became true. */
  pop?: boolean;
  /** One-shot halo, for an action that just landed. */
  ping?: boolean;
};

/** Small tinted badge with a glazed highlight. */
export const GlassBadge = styled(Box, {
  shouldForwardProp: (prop) =>
    !['tone', 'size', 'rounded', 'pulse', 'pop', 'ping'].includes(prop as string),
})<BadgeProps>(({ theme, tone = 'primary', size = 'md', rounded, pulse, pop, ping }) => ({
  ...centered,
  ...bgGradient({
    direction: '160deg',
    startColor: alpha(theme.palette[tone].main, 0.22),
    endColor: alpha(theme.palette[tone].main, 0.08),
  }),
  position: 'relative',
  width: MARK[size],
  height: MARK[size],
  borderRadius: rounded ? theme.shape.borderRadiusLg : '50%',
  border: `1px solid ${alpha(theme.palette[tone].main, 0.16)}`,
  color: toneText(theme, tone),
  // Gloss sits on ::before so ::after stays free for the halo — and neither is
  // clipped, since the highlight inherits the radius instead of being masked.
  '&::before': gloss(theme),
  ...(pulse && { ...ring(theme, tone, 0.3), animation: `${breathe} 1.6s ease-in-out infinite` }),
  ...(pop && { animation: `${popIn} 0.4s ease-out` }),
  ...(ping && {
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      ...ring(theme, tone, 0.4),
      animation: `${pingOnce} 0.7s ease-out`,
    },
  }),
}));

/** Frosted card for anything that interrupts the conversation. */
export const GlassCard = styled(Box)(({ theme }) => ({
  ...glassSurface(theme),
  borderRadius: theme.shape.borderRadiusLg,
  padding: theme.spacing(2),
  animation: `${cardIn} 0.18s ease-out`,
}));

/** The large accent mark that opens the empty state. */
export const HeroMark = styled(Box)(({ theme }) => ({
  ...centered,
  ...accentGradient(theme),
  position: 'relative',
  width: MARK.xl,
  height: MARK.xl,
  borderRadius: theme.shape.borderRadiusLg,
  color: theme.palette.common.white,
  boxShadow: theme.customShadows.primary,
  animation: `${glowPulse} 4s ease-in-out infinite`,
  '&::after': gloss(theme),
}));

/** Icons inside a glossy surface need lifting above the highlight layer. */
export const aboveGloss: React.CSSProperties = { position: 'relative' };
